import React, { useCallback, useState } from "react";

import { Recipe, recipesApi, sessionsApi, stockApi, Week, weeksApi } from "@/client/api";
import { Button, ConfirmModal, ErrorMessage, TopLoadingBar } from "@/client/components/ui";
import { RecipePickerModal } from "@/client/components/ui/RecipePickerModal";
import {
  buildMapsWithInference,
  buildNeedsStockSet,
  computeRecipesToPrep,
  getMissingRecipes,
  unplannedConsumptionsForDay,
} from "@/client/computations/meal-plan";
import { useDataVersion } from "@/client/context/DataVersionContext";
import { useToast } from "@/client/context/ToastContext";
import { useApi } from "@/client/hooks/useApi";
import { navigate } from "@/client/hooks/useRouter";
import { useWeekNavigation } from "@/client/hooks/useWeekNavigation";
import { AddIcon, CopyIcon, GroceryIcon, PlanIcon, PrepIcon } from "@/client/icons";
import { ConsumeModal } from "@/client/pages/plan/ConsumeModal";
import { DayCard } from "@/client/pages/plan/DayCard";
import { StockCoverageBadge } from "@/client/pages/plan/StockCoverageBadge";
import {
  addWeeks,
  buildStockMap,
  dateStrForDayIndex,
  DAYS,
  DAYS_ES_FULL,
  formatWeekRange,
  isNotPastWeek,
  todayDayOfWeek,
} from "@/client/pages/plan/types";
import { WeekGroceryDrawer } from "@/client/pages/plan/WeekGroceryDrawer";
import { WeekPicker } from "@/client/pages/plan/WeekPicker";
import { toDateStr } from "@/shared/date";

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PlanPage({ initialStartDate }: { initialStartDate?: string } = {}) {
  const { dataVersion } = useDataVersion();
  const { toast } = useToast();
  const { targetDate, displayStartDate, isCurrentWeek, navigateWeek, goToCurrentWeek, selectWeek } =
    useWeekNavigation(initialStartDate);
  const [pickerTarget, setPickerTarget] = useState<{
    day: string;
    mealType: "lunch" | "dinner";
  } | null>(null);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [copying, setCopying] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [consumeTarget, setConsumeTarget] = useState<{
    day: string;
    mealType: string;
    recipeId: string;
  } | null>(null);
  const [showWeekPicker, setShowWeekPicker] = useState(false);
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // The actual displayed week: either from targetDate, or current
  const weekFetcher = useCallback(async () => {
    void dataVersion; // cache-buster: recreate callback when data changes
    if (targetDate) {
      const list = await weeksApi.list();
      const existing = list.find((w) => w.start_date === targetDate);
      if (existing) return weeksApi.get(existing.id);
      return null;
    }
    return weeksApi.current();
  }, [targetDate, dataVersion]);

  const allWeeksFetcher = useCallback(() => {
    void dataVersion;
    return weeksApi.list();
  }, [dataVersion]);
  const recipesFetcher = useCallback(() => recipesApi.list(), []);
  const stockFetcher = useCallback(() => {
    void dataVersion;
    return stockApi.list();
  }, [dataVersion]);
  const {
    data: week,
    loading: weekLoading,
    error: weekError,
    refetch: refetchWeek,
    mutate: mutateWeek,
  } = useApi(weekFetcher as () => Promise<Week | null>, [targetDate, dataVersion]);
  const { data: allWeeks, refetch: refetchAllWeeks } = useApi(allWeeksFetcher, [dataVersion]);
  const { data: recipes } = useApi(recipesFetcher, []);
  const { data: stock, refetch: refetchStock } = useApi(stockFetcher, [dataVersion]);

  // Fetch consumptions
  const consumptionsFetcher = useCallback(() => {
    void dataVersion;
    if (!week) return Promise.resolve([]);
    const end = new Date(week.start_date + "T00:00:00");
    end.setDate(end.getDate() + 6);
    return stockApi.consumptions(week.start_date, toDateStr(end));
  }, [week, dataVersion]);
  const { data: consumptions, refetch: refetchConsumptions } = useApi(consumptionsFetcher, [
    week?.start_date,
    dataVersion,
  ]);

  const today = todayDayOfWeek();
  const baseStockMap = buildStockMap(stock ?? []);
  const { stockMap, consumptionMap } = buildMapsWithInference(
    baseStockMap,
    recipes ?? [],
    consumptions ?? [],
    week,
  );

  function getSlot(day: string, mealType: "lunch" | "dinner") {
    return week?.slots.find((s) => s.day_of_week === day && s.meal_type === mealType) ?? null;
  }

  function getSlotRecipes(day: string, mealType: "lunch" | "dinner") {
    const slot = getSlot(day, mealType);
    return slot?.recipes ?? [];
  }

  /** Optimistically update a slot in the local week data */
  function optimisticSlotUpdate(
    day: string,
    mealType: "lunch" | "dinner",
    updater: (slot: Week["slots"][0] | null) => Week["slots"][0] | null,
  ) {
    mutateWeek((prev) => {
      if (!prev) return prev;
      const existing = prev.slots.find((s) => s.day_of_week === day && s.meal_type === mealType);
      const updated = updater(existing ?? null);
      if (!updated && !existing) return prev;
      if (!updated) return { ...prev, slots: prev.slots.filter((s) => s !== existing) };
      if (!existing) return { ...prev, slots: [...prev.slots, updated] };
      return { ...prev, slots: prev.slots.map((s) => (s === existing ? updated : s)) };
    });
  }

  async function handleAdd(day: string, mealType: "lunch" | "dinner", recipe: Recipe) {
    if (!week) return;
    const slot = getSlot(day, mealType);
    const current = slot?.recipes ?? [];
    const newRecipe = {
      slot_id: slot?.id ?? "",
      recipe_id: recipe.id,
      servings: 1,
      recipe_name: recipe.name,
      recipe_type: recipe.type ?? "",
    };
    optimisticSlotUpdate(day, mealType, (s) => ({
      id: s?.id ?? "",
      day_of_week: day,
      meal_type: mealType as "lunch" | "dinner",
      note: s?.note ?? null,
      recipes: [...current, newRecipe],
    }));
    try {
      const apiRecipes = [
        ...current.map((r) => ({ recipe_id: r.recipe_id, servings: r.servings })),
        { recipe_id: recipe.id, servings: 1 },
      ];
      await weeksApi.setSlots(week.id, [
        { day_of_week: day, meal_type: mealType, recipes: apiRecipes },
      ]);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
    void refetchWeek();
  }

  async function handleRemove(day: string, mealType: "lunch" | "dinner", recipeId: string) {
    if (!week) return;
    const slot = getSlot(day, mealType);
    if (!slot) return;
    const remaining = slot.recipes.filter((r) => r.recipe_id !== recipeId);
    try {
      if (remaining.length === 0 && !slot.note) {
        optimisticSlotUpdate(day, mealType, () => null);
        await weeksApi.deleteSlot(week.id, slot.id);
      } else {
        optimisticSlotUpdate(day, mealType, (s) => (s ? { ...s, recipes: remaining } : null));
        await weeksApi.setSlots(week.id, [
          {
            day_of_week: day,
            meal_type: mealType,
            recipes: remaining.map((r) => ({ recipe_id: r.recipe_id, servings: r.servings })),
          },
        ]);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
    void refetchWeek();
  }

  function dayToDate(day: string): string {
    const dayIndex = DAYS.indexOf(day as (typeof DAYS)[number]);
    if (dayIndex < 0) return toDateStr(new Date());
    const d = new Date(displayStartDate + "T00:00:00");
    d.setDate(d.getDate() + dayIndex);
    return toDateStr(d);
  }

  async function handleChangeServings(
    day: string,
    mealType: "lunch" | "dinner",
    recipeId: string,
    delta: number,
  ) {
    if (!week) return;
    optimisticSlotUpdate(day, mealType, (s) =>
      s
        ? {
            ...s,
            recipes: s.recipes.map((r) =>
              r.recipe_id === recipeId ? { ...r, servings: Math.max(1, r.servings + delta) } : r,
            ),
          }
        : null,
    );
    try {
      const current = getSlotRecipes(day, mealType);
      const updated = current.map((r) => ({
        recipe_id: r.recipe_id,
        servings: r.recipe_id === recipeId ? Math.max(1, r.servings + delta) : r.servings,
      }));
      await weeksApi.setSlots(week.id, [
        { day_of_week: day, meal_type: mealType, recipes: updated },
      ]);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
    void refetchWeek();
  }

  function handleConsumeClick(day: string, mealType: "lunch" | "dinner", recipeId: string) {
    setConsumeTarget({ day, mealType, recipeId });
  }

  async function handleSetNote(day: string, mealType: "lunch" | "dinner", note: string | null) {
    if (!week) return;
    const slot = getSlot(day, mealType);
    const current = slot?.recipes ?? [];
    try {
      if (current.length === 0 && !note) {
        optimisticSlotUpdate(day, mealType, () => null);
        if (slot) await weeksApi.deleteSlot(week.id, slot.id);
      } else {
        optimisticSlotUpdate(day, mealType, (s) => ({
          id: s?.id ?? "",
          day_of_week: day,
          meal_type: mealType as "lunch" | "dinner",
          note,
          recipes: current,
        }));
        await weeksApi.setSlots(week.id, [
          {
            day_of_week: day,
            meal_type: mealType,
            note,
            recipes: current.map((r) => ({ recipe_id: r.recipe_id, servings: r.servings })),
          },
        ]);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
    void refetchWeek();
  }

  async function submitConsumption(servings: number) {
    if (!consumeTarget) return;
    const recipe = recipes?.find((r) => r.id === consumeTarget.recipeId);
    const hasComponents = (recipe?.component_recipes?.length ?? 0) > 0;
    const info = stockMap.get(consumeTarget.recipeId);

    if (!hasComponents && !info?.oldestBatchId) {
      toast("Sin stock disponible", "error");
      setConsumeTarget(null);
      return;
    }
    try {
      if (hasComponents) {
        // Recipe-level consumption: deducts own batches (if storable) + component batches
        await stockApi.consume({
          recipe_id: consumeTarget.recipeId,
          servings_consumed: servings,
          consumption_date: dayToDate(consumeTarget.day),
        });
      } else {
        await stockApi.consume({
          batch_id: info!.oldestBatchId!,
          servings_consumed: servings,
          consumption_date: dayToDate(consumeTarget.day),
        });
      }
      toast(`${servings} ${servings !== 1 ? "porciones registradas" : "porción registrada"}`);
      void refetchStock();
      void refetchConsumptions();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setConsumeTarget(null);
    }
  }

  async function handleDeleteConsumptions(ids: string[]) {
    try {
      for (const id of ids) await stockApi.deleteConsumption(id);
      toast(ids.length === 1 ? "Consumo eliminado" : `${ids.length} consumos eliminados`);
      void refetchStock();
      void refetchConsumptions();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
  }

  async function handleCopyToNextWeek() {
    if (!week) return;
    setCopying(true);
    try {
      const nextDate = addWeeks(week.start_date, 1);
      await weeksApi.copyWeek(week.id, nextDate);
      toast("Semana copiada");
      setShowCopyConfirm(false);
      void refetchAllWeeks();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error al copiar", "error");
    } finally {
      setCopying(false);
    }
  }

  async function handleCreateWeek() {
    try {
      await weeksApi.create(displayStartDate);
      toast("Semana creada");
      void refetchWeek();
      void refetchAllWeeks();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    }
  }

  async function handleCreateSessionFromMissing() {
    if (!week || recipesToPrep.length === 0) return;
    setCreatingSession(true);
    try {
      const { id } = await sessionsApi.create({ date: displayStartDate, status: "planned" });
      for (const r of recipesToPrep) {
        await sessionsApi.addBatch(id, {
          recipe_id: r.recipe_id,
          servings_produced: r.servings_to_cook,
          prep_date: displayStartDate,
        });
      }
      toast(
        `Sesión planificada con ${recipesToPrep.length} ${recipesToPrep.length !== 1 ? "recetas" : "receta"}`,
      );
      navigate(`/sesiones/${id}`);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Error", "error");
    } finally {
      setCreatingSession(false);
    }
  }

  if (weekLoading) return <TopLoadingBar visible />;
  if (weekError) return <ErrorMessage message={weekError} onRetry={refetchWeek} />;

  const notPast = isNotPastWeek(displayStartDate);
  const showStockCoverage = week && notPast && week.slots.length > 0;
  const needsStock = buildNeedsStockSet(recipes ?? []);
  const missingRecipes =
    week && notPast ? getMissingRecipes(week, stockMap, consumptionMap, needsStock) : [];

  const recipesToPrep = computeRecipesToPrep(missingRecipes, recipes ?? [], stockMap);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl text-term-text flex items-center gap-2.5">
            <span className="text-term-accent-dim">
              <PlanIcon width={22} height={22} />
            </span>
            Plan semanal
          </h1>
          <div className="flex items-center gap-2 mt-1 ml-[32.5px]">
            {/* Week picker */}
            <div className="relative">
              <button
                onClick={() => setShowWeekPicker((s) => !s)}
                className="text-sm text-term-text-dim hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1"
              >
                {formatWeekRange(displayStartDate)}
                <span className="text-micro">▾</span>
              </button>
              {showWeekPicker && allWeeks && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowWeekPicker(false)} />
                  <WeekPicker
                    weeks={allWeeks}
                    currentStartDate={displayStartDate}
                    onSelect={(d) => selectWeek(d)}
                    onClose={() => setShowWeekPicker(false)}
                  />
                </>
              )}
            </div>
            {isCurrentWeek && (
              <span className="text-micro text-term-user font-medium bg-term-user/10 px-1.5 py-0.5 rounded">
                esta semana
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {showStockCoverage && (
            <StockCoverageBadge
              week={week}
              stockMap={stockMap}
              consumptionMap={consumptionMap}
              needsStock={needsStock}
            />
          )}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigateWeek(-1)}>
              <span className="text-base leading-none">‹</span>
            </Button>
            <Button
              variant={isCurrentWeek ? "secondary" : "ghost"}
              size="sm"
              onClick={goToCurrentWeek}
            >
              Hoy
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigateWeek(1)}>
              <span className="text-base leading-none">›</span>
            </Button>
          </div>
          {week && (
            <>
              <Button variant="secondary" size="sm" onClick={() => setShowGroceryList(true)}>
                <GroceryIcon width={14} height={14} />
                Ingredientes
              </Button>
              <div className="relative">
                <Button variant="ghost" size="sm" onClick={() => setShowMoreMenu((s) => !s)}>
                  <span className="text-base leading-none">⋯</span>
                </Button>
                {showMoreMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 bg-term-surface border border-term-border rounded-xl shadow-xl py-1 min-w-48 max-w-[calc(100vw-2rem)]">
                      {recipesToPrep.length > 0 && (
                        <button
                          onClick={() => {
                            setShowMoreMenu(false);
                            void handleCreateSessionFromMissing();
                          }}
                          disabled={creatingSession}
                          className="w-full text-left px-4 py-2 text-sm text-term-text-dim hover:bg-term-raised hover:text-term-text cursor-pointer transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                          <PrepIcon width={14} height={14} className="text-term-accent shrink-0" />
                          {creatingSession
                            ? "Creando..."
                            : `Cocinar ${recipesToPrep.length} faltante${recipesToPrep.length !== 1 ? "s" : ""}`}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          setShowCopyConfirm(true);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-term-text-dim hover:bg-term-raised hover:text-term-text cursor-pointer transition-colors flex items-center gap-2"
                      >
                        <CopyIcon
                          width={14}
                          height={14}
                          className="text-term-text-muted shrink-0"
                        />
                        Copiar a semana sig.
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Week doesn't exist yet */}
      {!week && !weekLoading && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <p className="text-term-text-dim text-sm">
            No hay plan para la semana del {formatWeekRange(displayStartDate)}.
          </p>
          <Button variant="primary" onClick={handleCreateWeek}>
            <AddIcon width={14} height={14} />
            Crear semana
          </Button>
        </div>
      )}

      {/* Day cards grid */}
      {week && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {DAYS.map((day, i) => {
            const lunchRecipes = getSlotRecipes(day, "lunch");
            const dinnerRecipes = getSlotRecipes(day, "dinner");
            const plannedIds = new Set([...lunchRecipes, ...dinnerRecipes].map((r) => r.recipe_id));
            for (const id of [...plannedIds]) {
              const rec = recipes?.find((r) => r.id === id);
              for (const c of rec?.component_recipes ?? []) plannedIds.add(c.recipe_id);
            }
            const unplannedConsumptions = unplannedConsumptionsForDay(
              consumptions ?? [],
              dateStrForDayIndex(week.start_date, i),
              plannedIds,
            );
            return (
              <DayCard
                key={day}
                day={day}
                dayIndex={i}
                startDate={week.start_date}
                isToday={isCurrentWeek && day === today}
                stockMap={stockMap}
                consumptionMap={consumptionMap}
                lunchRecipes={lunchRecipes}
                dinnerRecipes={dinnerRecipes}
                lunchNote={getSlot(day, "lunch")?.note ?? null}
                dinnerNote={getSlot(day, "dinner")?.note ?? null}
                unplannedConsumptions={unplannedConsumptions}
                needsStock={notPast ? needsStock : undefined}
                onAdd={(d, m) => setPickerTarget({ day: d, mealType: m })}
                onRemove={handleRemove}
                onConsume={handleConsumeClick}
                onChangeServings={handleChangeServings}
                onSetNote={handleSetNote}
                onDeleteConsumptions={handleDeleteConsumptions}
              />
            );
          })}
        </div>
      )}

      {/* Picker modal */}
      {pickerTarget && recipes && (
        <RecipePickerModal
          recipes={recipes}
          onPick={(r) => {
            void handleAdd(pickerTarget.day, pickerTarget.mealType, r);
            setPickerTarget(null);
          }}
          onClose={() => setPickerTarget(null)}
        />
      )}

      {/* Consumption modal */}
      {consumeTarget && week && (
        <ConsumeModal
          recipeName={
            week.slots.flatMap((s) => s.recipes).find((r) => r.recipe_id === consumeTarget.recipeId)
              ?.recipe_name ?? ""
          }
          day={DAYS_ES_FULL[consumeTarget.day] ?? consumeTarget.day}
          stock={stockMap.get(consumeTarget.recipeId)?.servings ?? 0}
          existing={(consumptions ?? []).filter(
            (c) =>
              c.recipe_id === consumeTarget.recipeId &&
              c.consumption_date === dayToDate(consumeTarget.day),
          )}
          onSubmit={(n) => void submitConsumption(n)}
          onChanged={() => {
            void refetchStock();
            void refetchConsumptions();
          }}
          onClose={() => setConsumeTarget(null)}
        />
      )}

      {/* Weekly grocery list drawer */}
      {showGroceryList && week && (
        <WeekGroceryDrawer weekId={week.id} onClose={() => setShowGroceryList(false)} />
      )}

      {/* Copy confirm */}
      {showCopyConfirm && week && (
        <ConfirmModal
          message={`¿Copiar el plan de esta semana a la semana del ${formatWeekRange(addWeeks(week.start_date, 1))}? Se reemplazará el plan existente.`}
          confirmLabel={copying ? "Copiando..." : "Copiar"}
          onConfirm={() => void handleCopyToNextWeek()}
          onCancel={() => setShowCopyConfirm(false)}
        />
      )}
    </div>
  );
}
